/**
 * CloudflareSetupWizard — public, unauth wizard driven by the site_key.
 * Three steps: pick path → configure → verify + harden.
 *
 * Accessed at /cloudflare-setup/{site_key}. Added to AppHostGate
 * PUBLIC_PATHS so anonymous visitors on app.* domains can hit it.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cloud, Shield, Copy, CheckCircle2, AlertTriangle,
          Loader2, ExternalLink, KeyRound, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function CloudflareSetupWizard() {
  const { siteKey } = useParams();
  const [state, setState] = useState({ loading: true, err: null, invite: null, registrar_hint: null });
  const [step, setStep] = useState(1);
  const [chosenPath, setChosenPath] = useState(null); // 'hosted' | 'own'
  const [clientToken, setClientToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [detected, setDetected] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [whitelistResult, setWhitelistResult] = useState(null);
  const [saasResult, setSaasResult] = useState(null);

  async function addSaasHostname() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cf-setup/${siteKey}/saas-add-hostname`, {});
      setSaasResult(data);
      toast.success("Registered for shield. Add the CNAME to activate.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function pollSaasStatus() {
    setBusy(true);
    try {
      const { data } = await api.get(`/cf-setup/${siteKey}/saas-status`);
      setSaasResult((cur) => ({ ...cur, ...data }));
      if (data.status === "active" && data.ssl_status === "active") {
        toast.success("Shield is active on your hostname!");
        await load();
      } else {
        toast.info(`Status: ${data.status}, SSL: ${data.ssl_status}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }


  async function load() {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/cf-setup/${siteKey}`);
      const inv = data.invite;
      // Advance step automatically based on current status.
      let s = 1;
      if (inv.path && (inv.cf_zone_id || inv.status === "verified"
                          || inv.status === "hardened")) {
        s = 3;
      } else if (inv.path) {
        s = 2;
      }
      setStep(s);
      setChosenPath(inv.path || null);
      setState({ loading: false, err: null, invite: inv,
                     registrar_hint: data.registrar_hint });
    } catch (e) {
      setState({ loading: false, invite: null, registrar_hint: null,
                     err: formatApiError(e.response?.data?.detail) || e.message });
    }
  }
  useEffect(() => { load(); }, [siteKey]);

  async function detect() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cf-setup/${siteKey}/detect`, {});
      setDetected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function createZone() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cf-setup/${siteKey}/create-zone`, {});
      toast.success("Zone created on Cloudflare — see the nameservers below.");
      await load();
      setStep(3);
      setVerifyResult({ ...data, hardened: false, zone_status: data.status });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function storeClientToken() {
    if (!clientToken.trim()) { toast.error("Paste your Cloudflare token"); return; }
    setBusy(true);
    try {
      await api.post(`/cf-setup/${siteKey}/store-token`, { token: clientToken.trim() });
      toast.success("Token accepted. Now verifying + hardening the zone…");
      setClientToken("");
      await load();
      setStep(3);
      await verify();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function verify() {
    setBusy(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post(`/cf-setup/${siteKey}/verify`, {});
      setVerifyResult(data);
      if (data.hardened) {
        toast.success("Shield active. Your site is protected.");
      } else if (data.zone_status !== "active") {
        toast.info("Zone still pending. Retry after nameservers propagate.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function whitelistMyIp() {
    setBusy(true);
    try {
      const { data } = await api.post(`/cf-setup/${siteKey}/whitelist-my-ip`, {});
      setWhitelistResult(data);
      toast.success(`Your IP ${data.your_ip} is whitelisted across ${data.sync?.zones_updated ?? 0} zones.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function copy(txt, label = "Copied") {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(label);
    } catch { toast.error("Copy failed — please select the text manually."); }
  }

  const invite = state.invite;
  const registrarHint = state.registrar_hint;
  const nameservers = invite?.cf_nameservers || [];

  const zoneOnCloudflare = useMemo(() =>
    !!(detected?.nameservers || []).some(n => n.endsWith(".ns.cloudflare.com")),
    [detected]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }
  if (state.err || !invite) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
        <div className="max-w-md bg-white border border-red-200 rounded-lg p-6"
             data-testid="cf-setup-error">
          <AlertTriangle className="w-6 h-6 text-red-600 mb-2" />
          <h1 className="text-lg font-semibold text-neutral-900">Setup link invalid</h1>
          <p className="text-sm text-neutral-600 mt-1">
            {state.err || "This setup link doesn\u2019t exist or has been revoked."}{" "}
            Contact HustleSuite support to re-issue.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4"
         data-testid="cf-setup-wizard">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Cloud className="w-7 h-7 text-orange-600" />
          <h1 className="text-2xl font-semibold text-neutral-900">
            Set up Cloudflare protection
          </h1>
        </div>
        <p className="text-sm text-neutral-600 mb-6">
          Domain: <strong className="text-neutral-900">{invite.domain}</strong>
          {" · Managed by HustleSuite / HustleClients"}
        </p>

        {/* Progress bar */}
        <div className="flex items-center mb-8" data-testid="cf-setup-progress">
          {[1, 2, 3].map(n => (
            <div key={n} className="flex-1 flex items-center">
              <div className={
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium " +
                (step >= n
                  ? "bg-orange-600 text-white"
                  : "bg-neutral-200 text-neutral-500")
              }>
                {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
              </div>
              {n < 3 && (
                <div className={
                  "flex-1 h-0.5 mx-1 " +
                  (step > n ? "bg-orange-600" : "bg-neutral-200")
                }/>
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Pick a path */}
        {step === 1 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6"
               data-testid="cf-setup-step-1">
            <h2 className="text-lg font-semibold mb-2">Step 1 · Choose a setup path</h2>
            <p className="text-sm text-neutral-600 mb-4">
              You need Cloudflare in front of your website so we can block
              brute-force logins, XML-RPC probes, and DDoS traffic before
              they reach your server. Pick the option that fits your setup:
            </p>

            <div className="grid grid-cols-1 gap-3">
              {/* Path C — SaaS Custom Hostname (recommended for existing domains) */}
              <button
                onClick={() => { setChosenPath("saas"); setStep(2); }}
                data-testid="cf-setup-choose-saas"
                className="text-left p-4 border-2 border-emerald-500 rounded-md hover:shadow-md transition bg-emerald-50/40">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <div className="font-semibold text-neutral-900">
                    Quick shield — add just 1 DNS record
                  </div>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white font-medium">
                    Fastest · 2 minutes
                  </span>
                </div>
                <p className="text-xs text-neutral-700">
                  Keep your domain and DNS exactly where it is. Add
                  <strong> ONE CNAME record</strong> at your current DNS
                  host (Namecheap / GoDaddy / VentraIP / anywhere) that
                  points <code className="mx-1 px-1 bg-white rounded border">www.{invite.domain}</code>
                  at our shared shield hostname. We handle TLS + WAF + hardening.
                </p>
                <div className="mt-2 text-xs text-emerald-700 font-medium">
                  ⚡ Recommended if you already own <strong>{invite.domain}</strong> and
                  don&rsquo;t want to touch nameservers.
                </div>
              </button>

              {/* Path B (existing hosted flow) — full CF on HustleClients account */}
              <button
                onClick={() => { setChosenPath("hosted"); setStep(2); }}
                data-testid="cf-setup-choose-hosted"
                className="text-left p-4 border border-neutral-200 rounded-md hover:border-orange-500 hover:shadow-sm transition">
                <div className="flex items-center gap-2 mb-1">
                  <Cloud className="w-5 h-5 text-orange-600" />
                  <div className="font-semibold text-neutral-900">
                    Host full DNS on HustleClients Cloudflare
                  </div>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                    5 minutes
                  </span>
                </div>
                <p className="text-xs text-neutral-600">
                  We move all DNS for <strong>{invite.domain}</strong> to Cloudflare.
                  You&rsquo;ll need to change <strong>2 nameservers</strong> at your
                  domain registrar. Slightly more work, but you get full CF
                  control (analytics, page rules, etc.).
                </p>
              </button>

              {/* Path A (own) — client has their own CF */}
              <button
                onClick={() => { setChosenPath("own"); setStep(2); }}
                data-testid="cf-setup-choose-own"
                className="text-left p-4 border border-neutral-200 rounded-md hover:border-blue-500 hover:shadow-sm transition">
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="w-5 h-5 text-blue-600" />
                  <div className="font-semibold text-neutral-900">
                    Use my own Cloudflare account
                  </div>
                  <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                    Advanced
                  </span>
                </div>
                <p className="text-xs text-neutral-600">
                  You already run Cloudflare. Paste an API token with
                  Zone:Read + Zone WAF:Edit scoped to <strong>{invite.domain}</strong>.
                </p>
              </button>
            </div>

            <div className="mt-4 text-xs text-neutral-500">
              Not sure? <strong>Quick shield</strong> works for 99% of clients
              and gets your site protected in ~2 minutes.
            </div>
          </div>
        )}

        {/* Step 2c — SaaS: add hostname + show CNAME instruction */}
        {step === 2 && chosenPath === "saas" && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6"
               data-testid="cf-setup-step-2-saas">
            <h2 className="text-lg font-semibold mb-2">Step 2 · Add ONE DNS record</h2>
            <p className="text-sm text-neutral-600 mb-4">
              We&rsquo;ll register <strong>www.{invite.domain}</strong> in Cloudflare&rsquo;s
              shared shield zone. Then you add one CNAME at your DNS host — done.
            </p>
            {!saasResult ? (
              <Button
                onClick={addSaasHostname} disabled={busy}
                data-testid="cf-setup-saas-add"
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Register www.{invite.domain} for shield
              </Button>
            ) : (
              <div>
                <div className="text-sm font-medium text-neutral-800 mb-2">
                  Add this CNAME at your DNS host (Namecheap / GoDaddy / etc):
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded font-mono text-sm">
                  <div>
                    <div className="text-xs text-neutral-500">Type</div>
                    <div className="font-semibold">CNAME</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Name</div>
                    <div className="font-semibold flex items-center gap-1">
                      {saasResult.hostname}
                      <button onClick={() => copy(saasResult.hostname, "Name copied")}
                        data-testid="cf-setup-saas-copy-name"
                        className="text-neutral-500 hover:text-neutral-800">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Value / Target</div>
                    <div className="font-semibold flex items-center gap-1 break-all">
                      {saasResult.cname_target}
                      <button onClick={() => copy(saasResult.cname_target, "Target copied")}
                        data-testid="cf-setup-saas-copy-target"
                        className="text-neutral-500 hover:text-neutral-800">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 mb-4">
                  Cloudflare will provision TLS + hardening automatically the
                  moment the CNAME is live at your DNS host (usually within a
                  few minutes).
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={pollSaasStatus} disabled={busy} variant="secondary"
                    data-testid="cf-setup-saas-check">
                    {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
                    Check status
                  </Button>
                  <div className="text-xs text-neutral-500 self-center">
                    Hostname: <strong>{saasResult.status || "pending"}</strong>
                    {" · "}
                    SSL: <strong>{saasResult.ssl_status || "pending"}</strong>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2a — Hosted: create zone + show nameservers */}
        {step === 2 && chosenPath === "hosted" && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6"
               data-testid="cf-setup-step-2-hosted">
            <h2 className="text-lg font-semibold mb-2">Step 2 · Create Cloudflare zone</h2>
            <p className="text-sm text-neutral-600 mb-4">
              We&rsquo;ll add <strong>{invite.domain}</strong> to the HustleClients
              Cloudflare account. In the next screen you&rsquo;ll see 2 nameservers
              to paste at your registrar.
            </p>
            {nameservers.length === 0 ? (
              <Button
                onClick={createZone} disabled={busy}
                data-testid="cf-setup-create-zone"
                className="bg-orange-600 hover:bg-orange-700 text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Cloudflare zone for {invite.domain}
              </Button>
            ) : (
              <div>
                <div className="text-sm font-medium text-neutral-800 mb-2">
                  Paste these 2 nameservers at your domain registrar:
                </div>
                <div className="space-y-2 mb-4">
                  {nameservers.map((ns, i) => (
                    <div key={ns}
                      className="flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded font-mono text-sm"
                      data-testid={`cf-setup-ns-${i}`}>
                      <span className="flex-1 text-neutral-900">{ns}</span>
                      <button onClick={() => copy(ns, "Nameserver copied")}
                        data-testid={`cf-setup-ns-copy-${i}`}
                        className="text-neutral-500 hover:text-neutral-800">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {registrarHint && (
                  <a href={registrarHint.url} target="_blank" rel="noopener noreferrer"
                     data-testid="cf-setup-registrar-link"
                     className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline mb-4">
                    Open {registrarHint.name} nameserver settings
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 mb-4">
                  <strong>Important:</strong> replace <em>both</em> existing
                  nameservers with the two above. Propagation usually takes
                  15 minutes to a few hours. Once done, click <em>Verify</em>.
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={detect} disabled={busy} variant="secondary"
                    data-testid="cf-setup-detect">
                    {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
                    Check current nameservers
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    data-testid="cf-setup-continue"
                    className="bg-orange-600 hover:bg-orange-700 text-white">
                    I&rsquo;ve changed the nameservers →
                  </Button>
                </div>
                {detected && (
                  <div className="mt-4 p-3 bg-neutral-50 border border-neutral-200 rounded text-sm">
                    <div className="font-medium text-neutral-800 mb-1">
                      Detected nameservers for {detected.domain}:
                    </div>
                    <ul className="list-disc pl-5 text-neutral-700 text-xs">
                      {(detected.nameservers || []).map(n => (
                        <li key={n} data-testid={`cf-setup-detected-${n}`}>{n}</li>
                      ))}
                    </ul>
                    <div className={"mt-2 text-xs font-medium " +
                      (zoneOnCloudflare ? "text-emerald-700" : "text-amber-700")}>
                      {zoneOnCloudflare
                        ? "✓ Nameservers are pointing at Cloudflare."
                        : "✗ Nameservers not on Cloudflare yet — still propagating."}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 2b — Own CF: paste token */}
        {step === 2 && chosenPath === "own" && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6"
               data-testid="cf-setup-step-2-own">
            <h2 className="text-lg font-semibold mb-2">Step 2 · Paste Cloudflare API token</h2>
            <ol className="list-decimal pl-5 text-sm text-neutral-700 space-y-1 mb-4">
              <li>Go to{" "}
                <a
                  href="https://dash.cloudflare.com/profile/api-tokens"
                  className="text-blue-700 hover:underline"
                  target="_blank" rel="noopener noreferrer">
                  dash.cloudflare.com → My Profile → API Tokens
                </a>{" "}
                and click <strong>Create Token</strong> → <strong>Create Custom Token</strong>.
              </li>
              <li>Permissions:{" "}
                <code className="bg-neutral-100 px-1 rounded">Zone → Zone → Read</code>{" "}
                +{" "}
                <code className="bg-neutral-100 px-1 rounded">Zone → Zone WAF → Edit</code>.
              </li>
              <li>Zone Resources:{" "}
                <code className="bg-neutral-100 px-1 rounded">Include → Specific zone → {invite.domain}</code>.
              </li>
              <li>Click <strong>Continue → Create Token</strong>, copy the token,
                and paste it below.</li>
            </ol>
            <Label className="text-xs text-neutral-600">Cloudflare API token</Label>
            <Input
              value={clientToken}
              onChange={(e) => setClientToken(e.target.value)}
              placeholder="paste your CF token here…"
              type="password"
              className="mb-3 font-mono text-sm"
              data-testid="cf-setup-own-token" />
            <Button
              onClick={storeClientToken} disabled={busy}
              data-testid="cf-setup-own-submit"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save token &amp; harden site
            </Button>
            <button
              type="button" onClick={() => setStep(1)}
              className="ml-3 text-xs text-neutral-500 hover:underline">
              ← Change setup path
            </button>
          </div>
        )}

        {/* Step 3 — Verify + apply hardening */}
        {step === 3 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6"
               data-testid="cf-setup-step-3">
            <h2 className="text-lg font-semibold mb-2">
              Step 3 · Verify &amp; enable HustleConnect Shield
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              Once your zone is active on Cloudflare, we&rsquo;ll apply the same
              4-rule WordPress hardening pack + AI-crawler allow rule we run
              on every HustleSuite property.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <Button
                onClick={verify} disabled={busy}
                data-testid="cf-setup-verify"
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify &amp; harden now
              </Button>
              <span className="text-xs text-neutral-500">
                Zone status: <strong>{invite.cf_zone_status || "n/a"}</strong>
                {"  ·  "}
                Overall: <strong>{invite.status}</strong>
              </span>
            </div>

            {/* If the invite is already hardened (revisit or fresh verify),
                show the shield status + whitelist offer. */}
            {(invite.status === "hardened" || verifyResult?.hardened) && (
              <div className="mt-3" data-testid="cf-setup-verify-result">
                <div className="p-3 rounded border text-sm bg-emerald-50 border-emerald-200 text-emerald-800">
                  <div className="flex items-center gap-2 font-semibold">
                    <Shield className="w-4 h-4" /> Shield active on {invite.domain}
                  </div>
                  <div className="text-xs mt-1">
                    Your site is now protected from brute-force logins,
                    user-enumeration, and XML-RPC abuse. AI crawlers
                    (GPTBot, ClaudeBot, PerplexityBot, etc.) are
                    allowed through.
                  </div>

                  {/* Post-hardening 1-click IP whitelist */}
                  <div className="mt-4 pt-4 border-t border-emerald-200">
                    <div className="text-sm font-medium text-emerald-900 mb-1">
                      Whitelist your office IP?
                    </div>
                    <div className="text-xs text-emerald-800 mb-2">
                      Skip the challenge screen when you log into
                      <code className="mx-1 px-1 bg-emerald-100 rounded">/wp-admin</code>
                      from your normal office/home network. You can
                      revoke this any time from HustleSuite.
                    </div>
                    {whitelistResult ? (
                      <div
                        className="text-xs text-emerald-900 bg-white border border-emerald-300 rounded p-2"
                        data-testid="cf-setup-whitelist-result">
                        <span className="font-mono">{whitelistResult.your_ip}</span>{" "}
                        whitelisted across{" "}
                        <strong>{whitelistResult.sync?.zones_updated ?? 0}</strong>{" "}
                        Cloudflare zone(s).
                      </div>
                    ) : (
                      <Button
                        onClick={whitelistMyIp} disabled={busy} size="sm"
                        data-testid="cf-setup-whitelist-my-ip"
                        className="bg-emerald-700 hover:bg-emerald-800 text-white">
                        {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
                        Whitelist my current IP
                      </Button>
                    )}
                  </div>
                </div>
                {Array.isArray(verifyResult?.rules) && verifyResult.rules.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs">
                    {verifyResult.rules.map((r, i) => (
                      <li key={i}
                        data-testid={`cf-setup-rule-${i}`}
                        className={"flex items-center gap-2 " +
                          (r.status === "ok" ? "text-emerald-700" : "text-red-700")}>
                        {r.status === "ok" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                        <span className="font-medium">{r.description}</span>
                        <span className="text-neutral-500">— {r.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Zone not yet active — show pending message */}
            {verifyResult && !verifyResult.hardened && invite.status !== "hardened" && (
              <div className="mt-3" data-testid="cf-setup-verify-result">
                <div className="p-3 rounded border text-sm bg-amber-50 border-amber-200 text-amber-800">
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="w-4 h-4" />
                    {verifyResult.zone_status !== "active"
                      ? "Zone not active yet"
                      : "Partial success — see below"}
                  </div>
                  {verifyResult.message && (
                    <div className="text-xs mt-1">{verifyResult.message}</div>
                  )}
                </div>
                {Array.isArray(verifyResult.rules) && verifyResult.rules.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs">
                    {verifyResult.rules.map((r, i) => (
                      <li key={i}
                        data-testid={`cf-setup-rule-${i}`}
                        className={"flex items-center gap-2 " +
                          (r.status === "ok" ? "text-emerald-700" : "text-red-700")}>
                        {r.status === "ok" ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                        <span className="font-medium">{r.description}</span>
                        <span className="text-neutral-500">— {r.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
